import { useSelector } from "react-redux";
import { MutableRefObject, useCallback } from "react";
import { Create } from "@remo-co/ui-core/src/icons/Create";
import { ExitToApp } from "@remo-co/ui-core/src/icons/ExitToApp";
import { Settings } from "@remo-co/ui-core/src/icons/Settings";
import { Business } from "@remo-co/ui-core/src/icons/Business";
import { Speed } from "@remo-co/ui-core/src/icons/Speed";
import { Today } from "@remo-co/ui-core/src/icons/Today";
import { Description } from "@remo-co/ui-core/src/icons/Description";
import { VideoLibrary } from "@remo-co/ui-core/src/icons/VideoLibrary";
import { FolderShared } from "@remo-co/ui-core/src/icons/FolderShared";
import { PrivacyTip } from "@remo-co/ui-core/src/icons/PrivacyTip";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { MenuItem } from "@remo-co/ui-core/src/components/MenuItem";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Popover } from "@remo-co/ui-core/src/components/Popover";
import { lightTheme } from "@remo-co/ui-core/src/theme/lightTheme";
import { IUser } from "modules/app/types";
import { useHistory } from "react-router";
import useAuth from "modules/auth/hooks/useAuth";
import { useI18n } from "i18n";
import {
  selectCurrentEventCode,
  selectCurrentEventId,
  selectEventCompany,
  selectIsEventManager,
} from "modules/event/selectors";
import Avatar from "modules/userAvatar/Avatar";
import useConfirmationDialog from "modules/dialogNotification/hooks/useConfirmationDialog";
import { getUserName } from "modules/userProfile";
import { useIsInEvent } from "modules/event";
import { selectMyCompany } from "modules/company/redux/selectors";
import { useAppDispatch } from "store/hooks";
import { openUserProfileWindow } from "modules/userProfileWindow/redux/userProfileWindowSlice";
import {
  selectIsAnonymousUser,
  selectIsCompanyOwner,
} from "modules/auth/redux/selectors";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { UserProfileMenuStatus } from "modules/eventActivityStatus/components/UserProfileMenuStatus";
import { selectEnableAgencyWhiteLabelling } from "modules/settings/redux/selectors";
import { showCameraSettingsDialog } from "modules/cameraSettings/redux/actions";
import { usePageSettingReducerActions } from "modules/pageSetting/context/pageSettingReducer";
import { TrialSignupMenuItem } from "../TrialSignupMenuItem";
import { styles } from "./styles";

interface Props {
  showMenu: boolean;
  toggleMenu: () => void;
  showSwitchCompaniesPopover: () => void;
  showSwitchCompaniesMenuOption: boolean;
  profileRef: MutableRefObject<null>;
  user?: IUser;
  appVersion?: string;
}

const UserProfileMenuPopover = ({
  showMenu,
  toggleMenu,
  showSwitchCompaniesPopover,
  showSwitchCompaniesMenuOption,
  profileRef,
  user,
  appVersion,
}: Props): JSX.Element => {
  const { t } = useI18n(["menu", "event", "url"]);
  const { push } = useHistory();
  const dispatch = useAppDispatch();
  const isEventManager = useSelector(selectIsEventManager);
  const eventId = useSelector(selectCurrentEventId);
  const eventCode = useSelector(selectCurrentEventCode);
  const company = useSelector(selectMyCompany);
  const enableAgencyWhiteLabelling = useSelector(
    selectEnableAgencyWhiteLabelling,
  );
  const { setPageTitle } = usePageSettingReducerActions();
  const eventCompany = useSelector(selectEventCompany) ?? undefined;
  const eventCompanyData =
    typeof eventCompany !== "string" ? eventCompany ?? undefined : undefined;
  const isInEvent = useIsInEvent();
  const confirmation = useConfirmationDialog({
    message: t("menu:message.sure"),
  });
  const isUserAnonymous = useSelector(selectIsAnonymousUser);

  const isCompanyOwner = useSelector(selectIsCompanyOwner);

  const { signOut } = useAuth();

  const handleAudioVideoSettingsClick = () => {
    dispatch(showCameraSettingsDialog());
    toggleMenu();
  };

  const showProfileEdit = useCallback(() => {
    toggleMenu();
    dispatch(openUserProfileWindow());
  }, [dispatch, toggleMenu]);

  const doLogout = useCallback(() => {
    toggleMenu();
    signOut();
  }, [toggleMenu, signOut]);

  const redirectOut = useCallback((url: string) => {
    const win = window.open(url, "_blank");
    if (win) {
      win.focus();
    }
  }, []);

  const showPrivacySettings = useCallback(() => {
    window.__ucCmp.showSecondLayer();
    toggleMenu();
  }, [toggleMenu]);

  const redirectToMyEvents = () => {
    setPageTitle("");
    push("/my-events");
  };

  return (
    <Popover
      anchorEl={profileRef.current}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={showMenu}
      slotProps={{
        paper: {
          sx: styles.menuPopOver,
        },
      }}
      onClose={toggleMenu}
    >
      <Container
        backgroundColor={lightTheme.palette.gray.lightShade}
        sx={styles.header}
        data-testid="up-popover-container"
      >
        <Container sx={styles.headerContainer}>
          <Container sx={styles.profileContainer}>
            <Box sx={styles.avatarContainer}>
              <Avatar user={user} data-testid="up-popover-avatar" />
            </Box>
            <Box sx={styles.userInfoContainer}>
              <Typography variant="h4" sx={styles.profileName}>
                {getUserName(user)}
              </Typography>
              <Typography variant="h5" sx={styles.text}>
                {user?.profile.headline}
              </Typography>
            </Box>
          </Container>
          <Button
            data-testid="up-popover-profile"
            variant="text"
            color="blue"
            sx={styles.editButton}
            onClick={showProfileEdit}
          >
            {t("menu:user.menu.edit")}
          </Button>
        </Container>
        <UserProfileMenuStatus />
      </Container>
      {!isUserAnonymous && <TrialSignupMenuItem />}
      <Container sx={styles.menuItemsContainer}>
        {isInEvent &&
          isEventManager &&
          !isUserAnonymous &&
          company &&
          eventCompanyData &&
          eventCompanyData.id === company.id && (
            <MenuItem
              onClick={() => {
                toggleMenu();
                push(`/e/${eventCode}/event/settings/${eventId}`);
              }}
              text={t("menu:user.menu.edit.event")}
              data-testid="up-popover-edit-event"
              icon={<Create sx={styles.text} />}
              sx={styles.text}
            />
          )}
        {!isUserAnonymous && (
          <MenuItem
            onClick={() =>
              isInEvent
                ? confirmation(redirectToMyEvents)
                : redirectToMyEvents()
            }
            text={t("menu:user.menu.my.events")}
            data-testid="up-popover-my-events"
            icon={<Today sx={styles.text} />}
            sx={styles.text}
          />
        )}
        {!isUserAnonymous && isCompanyOwner && (
          <MenuItem
            onClick={() =>
              isInEvent
                ? confirmation(() => push("/account-settings"))
                : push("/account-settings")
            }
            text={t("menu:user.menu.org.settings")}
            data-testid="up-popover-org-settings"
            icon={<Business sx={styles.text} />}
            sx={styles.text}
          />
        )}
        {showSwitchCompaniesMenuOption && (
          <MenuItem
            onClick={showSwitchCompaniesPopover}
            text={t("menu:user.menu.change.company")}
            icon={<FolderShared sx={styles.text} />}
            sx={styles.text}
          />
        )}
        {isInEvent && (
          <>
            <Box sx={styles.separator}>
              <Typography variant="subtitle1">
                {t("menu:user.menu.mic.cam")}
              </Typography>
            </Box>
            <MenuItem
              onClick={handleAudioVideoSettingsClick}
              text={t("menu:audio.video.settings")}
              icon={<Settings sx={styles.text} />}
              sx={styles.text}
            />
          </>
        )}
        <MenuItem
          onClick={() => redirectOut("/geartest")}
          text={t("menu:user.menu.geartest")}
          data-testid="up-popover-geartest"
          icon={<Speed sx={styles.text} />}
          sx={styles.text}
        />
        {!enableAgencyWhiteLabelling && (
          <>
            <Box sx={styles.separator}>
              <Typography variant="subtitle1" sx={styles.text}>
                {t("menu:user.menu.other")}
              </Typography>
            </Box>
            <MenuItem
              onClick={() => redirectOut(t("url:remo.helpdesk"))}
              text={t("event:resources.articles")}
              data-testid="up-popover-articles-help"
              icon={<Description sx={styles.text} />}
              sx={styles.text}
            />
            <MenuItem
              onClick={() => redirectOut(t("url:remo.video.articles"))}
              text={t("event:resources.tutorials")}
              data-testid="up-popover-video-help"
              icon={<VideoLibrary sx={styles.text} />}
              sx={styles.text}
            />
          </>
        )}
        <MenuItem
          onClick={showPrivacySettings}
          text={t("event:privacy.settings")}
          data-testid="up-popover-privacy-settings"
          icon={<PrivacyTip color="secondary" />}
        />
        <MenuItem
          onClick={doLogout}
          text={t("menu:user.menu.logout")}
          icon={<ExitToApp sx={styles.text} />}
          data-testid="up-popover-logout"
          sx={styles.text}
        />

        {appVersion && (
          <Box sx={styles.versionContainer}>
            <Typography
              color="textSecondary"
              variant="caption"
              data-testid="up-popover-version"
              sx={styles.version}
            >{`Version: ${appVersion}`}</Typography>
          </Box>
        )}
      </Container>
    </Popover>
  );
};

export default UserProfileMenuPopover;
